import React, { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, Check, Clock, MessageCircle, Phone, AlertCircle } from "lucide-react";
import { API, StatusBadge, AppointmentStatusBadge } from "./shared";

const ConfirmModal = ({ appointment, onClose, onConfirm, token }) => {
  const [date, setDate] = useState(appointment.consultation_date || "");
  const [time, setTime] = useState(appointment.consultation_slot || "");
  const [saving, setSaving] = useState(false);
  const [bookedInfo, setBookedInfo] = useState({ confirmed: [], requested: [] });

  const slots = ["5:00 PM - 5:30 PM", "5:30 PM - 6:00 PM", "6:00 PM - 6:30 PM", "6:30 PM - 7:00 PM"];

  useEffect(() => {
    if (!date) { setBookedInfo({ confirmed: [], requested: [] }); return; }
    axios.get(`${API}/admin/appointments/booked-slots?date=${date}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setBookedInfo(res.data))
      .catch(() => setBookedInfo({ confirmed: [], requested: [] }));
  }, [date, token]); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  const isConfirmed = (slot) => bookedInfo.confirmed.some(b => b.slot === slot);
  const isRequested = (slot) => bookedInfo.requested.some(b => b.slot === slot);
  const getBookedName = (slot) => {
    const c = bookedInfo.confirmed.find(b => b.slot === slot);
    if (c) return c.name;
    const r = bookedInfo.requested.find(b => b.slot === slot);
    return r ? r.name : null;
  };

  const handleSubmit = async () => {
    if (!date || !time) return;
    setSaving(true);
    await onConfirm(appointment.id, date, time);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" data-testid="confirm-modal">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
        <h3 className="font-serif text-xl text-emerald_deep mb-1">Confirm Appointment</h3>
        <p className="text-sm text-text_muted mb-6">for {appointment.customer_name}</p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-emerald_deep mb-1.5">Date</label>
            <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setTime(""); }}
              data-testid="confirm-date-input"
              className="w-full px-4 py-2.5 rounded-xl border border-emerald_deep/10 bg-bone/50 text-emerald_deep focus:outline-none focus:ring-2 focus:ring-accent/40" />
          </div>
          <div>
            <label className="block text-sm text-emerald_deep mb-1.5">Time Slot</label>
            <div className="grid grid-cols-2 gap-2">
              {slots.map(s => {
                const confirmed = isConfirmed(s);
                const requested = isRequested(s);
                const bookedName = getBookedName(s);
                return (
                  <button key={s} onClick={() => !confirmed && setTime(s)} data-testid={`confirm-slot-${s.replace(/\s/g, "-")}`}
                    disabled={confirmed}
                    className={`relative px-3 py-2 text-xs rounded-lg border transition-all ${
                      confirmed ? "border-red-200 bg-red-50 text-red-400 cursor-not-allowed" :
                      time === s ? "border-accent bg-accent/10 text-emerald_deep font-semibold" :
                      requested ? "border-amber-200 bg-amber-50 text-amber-700 hover:border-accent/40" :
                      "border-emerald_deep/10 text-text_muted hover:border-accent/40"
                    }`}>
                    <span>{s}</span>
                    {confirmed && <span className="block text-[10px] mt-0.5 text-red-400 truncate" title={bookedName}>Booked: {bookedName}</span>}
                    {!confirmed && requested && <span className="block text-[10px] mt-0.5 text-amber-600 truncate" title={bookedName}>Requested: {bookedName}</span>}
                  </button>
                );
              })}
            </div>
            {date && (bookedInfo.confirmed.length > 0 || bookedInfo.requested.length > 0) && (
              <div className="flex gap-4 mt-2 text-[10px] text-text_muted">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> Booked</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400"></span> Requested</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald_deep/20"></span> Available</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-emerald_deep/10 text-text_muted text-sm hover:bg-bone transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={!date || !time || saving} data-testid="confirm-appointment-btn"
            className="flex-1 px-4 py-2.5 rounded-xl bg-emerald_deep text-bone text-sm font-medium hover:bg-emerald_deep/90 transition-all disabled:opacity-50">
            {saving ? "Confirming..." : "Confirm & Notify"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AppointmentsTab({ appointments, token, onRefresh }) {
  const [confirmingAppt, setConfirmingAppt] = useState(null);
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all" ? appointments
    : appointments.filter(a => a.appointment_status === filter);

  const handleConfirm = async (id, date, time) => {
    try {
      await axios.post(`${API}/admin/appointments/confirm`, {
        enrollment_id: id,
        confirmed_date: date,
        confirmed_time: time
      }, { headers: { Authorization: `Bearer ${token}` } });
      setConfirmingAppt(null);
      onRefresh();
    } catch (err) {
      alert("Failed to confirm: " + (err.response?.data?.detail || err.message));
    }
  };

  const getWhatsAppLink = (appt, type) => {
    const phone = (appt.customer_phone || "").replace(/[^0-9]/g, "");
    const phoneNum = phone.startsWith("91") ? phone : `91${phone}`;
    if (type === "confirm") {
      return `https://wa.me/${phoneNum}?text=${encodeURIComponent(
        `Hi ${appt.customer_name}! Your Fitly India consultation has been confirmed for ${appt.confirmed_date} at ${appt.confirmed_time}. Please share your reports at least 1 day before. Looking forward to speaking with you!`
      )}`;
    }
    return `https://wa.me/${phoneNum}?text=${encodeURIComponent(
      `Hi ${appt.customer_name}! This is a reminder from Fitly India. Your consultation is coming up at ${appt.confirmed_time} today. Please have your reports ready. See you soon!`
    )}`;
  };

  const pendingCount = appointments.filter(a => a.appointment_status === "pending_confirmation" && a.status === "completed").length;

  return (
    <div data-testid="appointments-tab">
      {confirmingAppt && (
        <ConfirmModal
          appointment={confirmingAppt}
          onClose={() => setConfirmingAppt(null)}
          onConfirm={handleConfirm}
          token={token}
        />
      )}

      {pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl mb-4 flex items-center gap-2" data-testid="pending-alert">
          <AlertCircle size={16} />
          <span className="text-sm font-medium">{pendingCount} appointment{pendingCount > 1 ? "s" : ""} pending confirmation</span>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {[
          { id: "all", label: "All" },
          { id: "pending_confirmation", label: "Pending" },
          { id: "confirmed", label: "Confirmed" }
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`appt-filter-${f.id}`}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
              filter === f.id ? "bg-accent text-white" : "bg-bone text-text_muted hover:text-emerald_deep"
            }`}>{f.label}</button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-emerald_deep/5 p-8 text-center text-text_muted">
            <Calendar size={24} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">No appointments found</p>
          </div>
        ) : filtered.map((appt) => (
          <div key={appt.id} className="bg-white rounded-2xl border border-emerald_deep/5 shadow-sm p-5" data-testid={`appointment-card-${appt.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-semibold text-emerald_deep">{appt.customer_name}</p>
                <p className="text-xs text-text_muted">{appt.program_name} — ₹{(appt.amount / 100).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2">
                {StatusBadge(appt.status)}
                {AppointmentStatusBadge(appt.appointment_status || "pending_confirmation")}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-3">
              <div>
                <span className="text-text_muted">Phone</span>
                <p className="text-emerald_deep font-medium flex items-center gap-1"><Phone size={11} />{appt.customer_phone || "—"}</p>
              </div>
              <div>
                <span className="text-text_muted">Requested Date</span>
                <p className="text-emerald_deep font-medium">{appt.consultation_date || "—"}</p>
              </div>
              <div>
                <span className="text-text_muted">Requested Slot</span>
                <p className="text-emerald_deep font-medium">{appt.consultation_slot || "—"}</p>
              </div>
              <div>
                <span className="text-text_muted">Health Concern</span>
                <p className="text-emerald_deep font-medium truncate max-w-[150px]" title={appt.health_concern}>{appt.health_concern || "—"}</p>
              </div>
            </div>

            {appt.appointment_status === "confirmed" && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3 text-xs">
                <p className="text-green-700 font-semibold flex items-center gap-1"><Check size={12} /> Confirmed: {appt.confirmed_date} at {appt.confirmed_time}</p>
                {appt.reminder_sent && <p className="text-green-600 mt-1">Reminder sent</p>}
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              {appt.status === "completed" && appt.appointment_status === "pending_confirmation" && (
                <button onClick={() => setConfirmingAppt(appt)} data-testid={`confirm-btn-${appt.id}`}
                  className="px-4 py-2 bg-emerald_deep text-bone text-xs rounded-full font-medium hover:bg-emerald_deep/90 transition-all flex items-center gap-1.5">
                  <Check size={12} /> Confirm Slot
                </button>
              )}
              {appt.appointment_status === "confirmed" && (
                <>
                  <a href={getWhatsAppLink(appt, "confirm")} target="_blank" rel="noopener noreferrer"
                    data-testid={`wa-confirm-${appt.id}`}
                    className="px-4 py-2 bg-[#25D366] text-white text-xs rounded-full font-medium hover:bg-[#20BD5A] transition-all flex items-center gap-1.5">
                    <MessageCircle size={12} /> Send Confirmation
                  </a>
                  <a href={getWhatsAppLink(appt, "reminder")} target="_blank" rel="noopener noreferrer"
                    data-testid={`wa-remind-${appt.id}`}
                    className="px-4 py-2 border border-[#25D366] text-[#25D366] text-xs rounded-full font-medium hover:bg-[#25D366]/5 transition-all flex items-center gap-1.5">
                    <Clock size={12} /> Send Reminder
                  </a>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
